// Step2PlayersLevelPoints — ActivityWizard Steg 2 (delspår multi-bana). "Vilka + format":
// antal spelare + nivå + poäng (tournament-villkorlig). Moved here from step 3 in the
// multi-bana step-reorder (typ → spelare → bana → tid) so the court count N can be derived
// from the player count BEFORE the court/time step. Read-only — no booking created (3g).
//
// Player count is a multiple-of-4 stepper (beslut #2) capped at the auto-pool capacity
// (active courts with max_players >= 4, × 4 — beslut #3/#6). Caps so the hall can always fit
// the players. MultiCourtInfo moved to step 3 (rendered next to the court choice).
// Defaults from ActivityWizard initialState (static, no phantom-dirty per D4).

import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useActivityWizard } from "../ActivityWizardContext";
import { useActivityTypes } from "@/hooks/useActivityTypes";
import { useOrgHall } from "@/hooks/useOrgHall";
import { filterAutoPoolCourtIds, PADEL_MIN_COURT_CAPACITY } from "@/lib/wizardSlots";
import PlayerCountInput, { PLAYER_STEP } from "../PlayerCountInput";
import LevelRangeInput from "../LevelRangeInput";
import PointCountInput from "../PointCountInput";
import WizardStepHero from "../WizardStepHero";

type CourtRow = { id: string; max_players: number; status: string };

export default function Step2PlayersLevelPoints() {
  const { t } = useTranslation("admin");
  const { state, dispatch } = useActivityWizard();
  const { types } = useActivityTypes();
  const { courts } = useOrgHall();
  const fd = state.formData;

  const selectedType = types.find((tp) => tp.id === fd.activityTypeId) ?? null;
  const isTournament = !!selectedType?.tournament_format;

  // Cap = auto-pool capacity (courts with max_players >= 4, × 4). Floor at one court so the
  // stepper always has a valid range even if the org has no qualifying courts.
  const autoPoolSize = useMemo(() => filterAutoPoolCourtIds(courts as CourtRow[]).length, [courts]);
  const minPlayers = PLAYER_STEP; // 4 (beslut #2 — smallest padel activity)
  const maxPlayers = Math.max(minPlayers, autoPoolSize * PADEL_MIN_COURT_CAPACITY);

  const update = (payload: Partial<typeof fd>) => dispatch({ type: "UPDATE_FORM", payload });
  const heading = "mb-2 text-sm font-medium text-muted-foreground";

  return (
    <div className="space-y-4 md:space-y-6">
      <WizardStepHero
        title={t("activityWizard.step3.heroTitle", { defaultValue: "Vilka spelar?" })}
        subtitle={t("activityWizard.step3.heroSubtitle", {
          defaultValue: "Antal platser och nivå avgör vilka som kan vara med.",
        })}
      />
      <section>
        <h2 className={heading}>{t("activityWizard.step3.playerCountHeading", { defaultValue: "Antal spelare" })}</h2>
        <PlayerCountInput
          value={fd.spotsTotal ?? minPlayers}
          min={minPlayers}
          max={maxPlayers}
          onValueChange={(v) => update({ spotsTotal: v })}
        />
      </section>

      <section>
        <h2 className={heading}>{t("activityWizard.step3.levelHeading", { defaultValue: "Nivå" })}</h2>
        <LevelRangeInput
          minLevel={fd.minLevel ?? 1}
          maxLevel={fd.maxLevel ?? 10}
          onMinChange={(v) => update({ minLevel: v })}
          onMaxChange={(v) => update({ maxLevel: v })}
        />
      </section>

      {isTournament && (
        <section>
          <h2 className={heading}>{t("activityWizard.step3.pointsHeading", { defaultValue: "Antal poäng" })}</h2>
          <PointCountInput
            value={fd.targetPoints ?? selectedType?.default_points_per_match ?? 32}
            onValueChange={(v) => update({ targetPoints: v })}
          />
        </section>
      )}
    </div>
  );
}
